import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import SEO from "@/components/SEO";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { IMAGES } from "@/data/business";

const PHOTOS = [
  { ...IMAGES.bridalPortrait, cat: "Bridal", tall: true },
  { ...IMAGES.salonInterior, cat: "Salon", tall: false },
  { ...IMAGES.bridalMirror, cat: "Bridal", tall: true },
  { ...IMAGES.bridalDoor, cat: "Bridal", tall: true },
  { ...IMAGES.logo, cat: "Salon", tall: false },
];

const FILTERS = ["All", "Bridal", "Salon"];

export default function Gallery() {
  const [filter, setFilter] = useState("All");
  const [lightbox, setLightbox] = useState(null);
  const reduce = useReducedMotion();

  const visible = filter === "All" ? PHOTOS : PHOTOS.filter((p) => p.cat === filter);

  const close = useCallback(() => setLightbox(null), []);
  const step = useCallback(
    (dir) => setLightbox((cur) => (cur === null ? null : (cur + dir + visible.length) % visible.length)),
    [visible.length]
  );

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, close, step]);

  let touchX = null;

  return (
    <main className="bg-ivory">
      <SEO
        title="Gallery"
        description="A look inside Glam Girl Makeup Studio, Varanasi — bridal looks, makeup and the studio space in Patel Nagar / Hardattpur."
      />
      <section className="mx-auto max-w-7xl px-5 pb-24 pt-32 sm:px-8 lg:pt-40" data-testid="gallery-section">
        <Reveal>
          <Eyebrow>Gallery</Eyebrow>
          <h1 className="mt-4 max-w-2xl font-serif text-4xl font-medium leading-[1.08] tracking-tight text-charcoal sm:text-5xl lg:text-6xl">
            The studio, <em className="text-rose">in frames.</em>
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-charcoal/65">
            Real photographs from Glam Girl Makeup Studio — our brides, our space.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-10 flex gap-2" role="tablist" aria-label="Filter gallery">
            {FILTERS.map((f) => (
              <button
                key={f}
                role="tab"
                aria-selected={filter === f}
                onClick={() => { setFilter(f); setLightbox(null); }}
                data-testid={`gallery-filter-${f.toLowerCase()}`}
                className={`rounded-full px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] transition-colors ${
                  filter === f
                    ? "bg-charcoal text-ivory"
                    : "border border-charcoal/15 text-charcoal/60 hover:border-rose hover:text-rose"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-12 columns-2 gap-5 lg:columns-3" data-testid="gallery-grid">
          {visible.map((p, i) => (
            <Reveal key={p.src} delay={0.05 * i} className="mb-5 break-inside-avoid">
              <button
                onClick={() => setLightbox(i)}
                data-testid={`gallery-photo-${i}`}
                aria-label={`Open photo: ${p.alt}`}
                className="group block w-full overflow-hidden rounded-xl border border-charcoal/10"
              >
                <img
                  src={p.src}
                  alt={p.alt}
                  loading="lazy"
                  className={`w-full object-cover transition-transform duration-700 group-hover:scale-105 ${
                    p.tall ? "aspect-[3/4]" : "aspect-[4/3]"
                  }`}
                />
              </button>
            </Reveal>
          ))}
        </div>
      </section>

      <AnimatePresence>
        {lightbox !== null && visible[lightbox] && (
          <motion.div
            data-testid="gallery-lightbox"
            role="dialog"
            aria-modal="true"
            aria-label="Photo viewer"
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-ink/95 p-4 backdrop-blur-sm"
            onClick={close}
            onTouchStart={(e) => { touchX = e.touches[0].clientX; }}
            onTouchEnd={(e) => {
              if (touchX === null) return;
              const dx = e.changedTouches[0].clientX - touchX;
              if (Math.abs(dx) > 48) step(dx < 0 ? 1 : -1);
              touchX = null;
            }}
          >
            <button
              onClick={close}
              data-testid="lightbox-close"
              aria-label="Close photo viewer"
              className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full border border-ivory/25 text-ivory transition-colors hover:border-champagne hover:text-champagne"
            >
              <X className="h-5 w-5" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); step(-1); }}
              data-testid="lightbox-prev"
              aria-label="Previous photo"
              className="absolute left-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-ivory/25 text-ivory transition-colors hover:border-champagne hover:text-champagne sm:left-6"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <motion.figure
              key={visible[lightbox].src}
              initial={reduce ? false : { opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.35 }}
              className="max-h-full max-w-3xl"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={visible[lightbox].src}
                alt={visible[lightbox].alt}
                className="max-h-[78vh] w-auto rounded-lg object-contain"
              />
              <figcaption className="mt-4 text-center text-xs uppercase tracking-[0.22em] text-ivory/50">
                {visible[lightbox].cat} · {lightbox + 1} / {visible.length}
              </figcaption>
            </motion.figure>
            <button
              onClick={(e) => { e.stopPropagation(); step(1); }}
              data-testid="lightbox-next"
              aria-label="Next photo"
              className="absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-ivory/25 text-ivory transition-colors hover:border-champagne hover:text-champagne sm:right-6"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
